import { OnDestroy, Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MessageService } from 'primeng/api';

import { BlockTypeModel } from '../../../Common/PageBuilder/models/block-type.model';
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { WireService } from './wire.service';
import {
    wireBlockTypes,
    wireCompanyAddressBlockType,
    wireCompanyBioBlockType,
    wireCompanyLogoBlockType,
    wireContactBlockType,
    wireImageBlockType,
} from '../models/wire-block-types.model';
import { WireModel } from '../models/wire.model';
import { AbstractPageBuilderService } from '../../../Common/PageBuilder/services/abstract-page-builder.service';
import { StylesService } from '../../../Common/Styles/services/styles.service';
import { TwigRendererService } from '../../../Common/PageBuilder/services/twig-renderer.service';
import { GenericStyleModel } from '../../../Common/Styles/models/generic-style.model';
import { StyleStateEnum } from '../../../Common/PageBuilder/enums/style-state.enum';
import { AmplifyRoutingService } from '../../Core/services/amplify-routing.service';
import { AmplifyRouteModel } from '../../AmplifyRouter/models/amplify-route.model';
import { WireBlockTypeEnum } from '../enums/wire-block-type.enum';
import { WireCompanyLogoModel } from '../models/wire-company-logo.model';
import { WireCompanyAddressModel } from '../models/wire-company-address.model';
import { companyAddressTwigTemplate, companyLogoTwigTemplate, headerTwigTemplate } from '../models/wire-twig-template';
import { WireStyleStateEnum } from '../enums/wire-style-state.enum';
import { BlockValidationTypeEnum } from '../../../Common/PageBuilder/enums/block-validation-type.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { WireRegion } from '../../../api/wire/enums/wire-region.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';


@Injectable()
export class WirePageBuilderService extends AbstractPageBuilderService implements OnDestroy {

    private readonly imageBlockLimit: number = 10;
    private readonly companyBioBlockLimit: number = 1;
    private readonly contactBlockLimit: number = 3;

    private importDialogVisible: boolean = false;
    private importDialogVisibleProvider: BehaviorSubject<boolean>;
    private importToolDropdownSelectedBlockProvider: Subject<BlockModel>;
    private fakeBlockDataChangeProvider: BehaviorSubject<WireBlockTypeEnum> = new BehaviorSubject<WireBlockTypeEnum>(null);
    private blocksAreActionable: boolean = true;

    readonly globalProperty?: string = 'wire';
    focusedWire: WireModel;

    currentRoute: AmplifyRouteModel;

    /**
     * Class Constructor
     */
    constructor(
        public wireService: WireService,
        public stylesService: StylesService,
        public twigRenderService: TwigRendererService,
        public amplifyRoutingService: AmplifyRoutingService,
        public localizationService: LocalizationService,
        private messageService: MessageService,
    ) {
        super(stylesService, twigRenderService, localizationService);

        this.importDialogVisibleProvider = new BehaviorSubject<boolean>(null);
        this.importToolDropdownSelectedBlockProvider = new Subject<BlockModel>();
        this.subscriptions = [];
        this.setSubscriptions();
        this.setBlockTypes(wireBlockTypes);

        this.hasDefaultTemplates$.subscribe(
            hasTemplates => {
                if (hasTemplates) {
                    this.setBlockTypes(this.setDefaultTemplatesOnBlockTypes(wireBlockTypes));
                    this.setTemplatesOnBlocks(this.blocks);
                }
            }
        );

        this.setBlockTypeGroups([
            {
                blockTypes: wireBlockTypes
            }
        ]);
        this.setInlineAdderHeight(230);
    }

    ngOnDestroy() {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    setDefaultTemplatesOnBlockTypes(blockTypes: BlockTypeModel[]): BlockTypeModel[] {
        return blockTypes.map(blockType => {
            blockType.twig = this.getDefaultTemplateByBlockType(
                blockType.key,
                WireStyleStateEnum.NEWSWIRE_1
            );
            return blockType;
        });
    }

    /**
     * Handles changes made by the block form and updates the blocks to keep bound relationships in sync
     */
    blockFormUpdated(block: BlockModel, property: string, value: any): void {
        this.validateBlock(block);

        if (property !== 'styles') {
            this.formFieldUpdated();

            return;
        }

        if (block.displayElement.isBoundToGlobalStyles) {
            this.updateBoundBlocksOfSameType(block, property, value);
        }

        this.formFieldUpdated();
    }

    getDefaultTemplateByBlockType(blockType: string, defaultReference: string): string {
        if (defaultReference === StyleStateEnum.DEFAULT || !defaultReference) {
            defaultReference = WireStyleStateEnum.NEWSWIRE_1;
        }

        // If there is no template for the requested defaultReference, fall back to NEWSWIRE_1.  Not all the defaultReferences need their own template
        return super.getDefaultTemplateByBlockType(blockType, defaultReference) ||
            super.getDefaultTemplateByBlockType(blockType, WireStyleStateEnum.NEWSWIRE_1);
    }

    /**
     * Sets subscriptions, pushing to array for easy cleanup later
     */
    private setSubscriptions(): void {
        this.subscriptions.push(
            this.wireService.currentResource$.subscribe(
                currentWire => {
                    if (!currentWire) {
                        // TODO: loading here
                        return;
                    }

                    this.focusedWire = currentWire;

                    // if the selected block no longer exists, then deselect it
                    // fake blocks are not included in blocks object so we need to check them seperately
                    if (this.selectedBlock && !this.blockExists()
                        && ((this.selectedBlock.type.key === WireBlockTypeEnum.companyAddress && !this.focusedWire.companyAddress)
                            || (this.selectedBlock.type.key === WireBlockTypeEnum.companyLogo && !this.focusedWire.companyLogo))) {
                        // we don't need to cancel changes because it's not going to get saved anyway
                        this.deselectBlock(true);
                    }

                    // Don't call this.setBlocks because it calls setCurrentWireBlocks and results in a infinite loop
                    super.setBlocks(currentWire.blocks);
                    this.setStylesOnBlocks();
                    this.setTemplatesOnBlocks(this.blocks);
                    this.setBlocksActionable(this.blocksAreActionable);
                }
            ),
            this.amplifyRoutingService.getCurrentRoute().subscribe(x => this.currentRoute = x),
            this.wireService.isWireEditable$.subscribe(isEditable => {
                this.blocksAreActionable = isEditable;
                this.setBlocksActionable(this.blocksAreActionable);
            }),
        );
    }

    /**
     * Adds a block
     */
    public addBlock(block: BlockModel): void {
        this.blocks.push(block);
        this.setBlocks(this.blocks);
    }

    /**
     * Adds a block to the current list
     *
     * @param type A block type
     */
    addBlockOfType(type: BlockTypeModel, position?: string): void {
        const positionToAdd = position ? +position : null;
        // deal with fake block types, otherwise, call default addBlockOfType()
        switch (type.key){
            case WireBlockTypeEnum.companyLogo:
                this.focusedWire.companyLogo = new WireCompanyLogoModel();
                type.canAdd = false;
                this.wireService.currentResourceModified();
                break;
            case WireBlockTypeEnum.companyAddress:
                this.focusedWire.companyAddress = new WireCompanyAddressModel();
                type.canAdd = false;
                this.wireService.currentResourceModified();
                break;
            default:
                let block = new BlockModel(null, type, new type.displayElementType());
                // restrict some formatting options for newswire
                if (type.key === WireBlockTypeEnum.text) {
                    block = this.wireService.createNewTextBlock();
                }

                this.showMultimediaBlockWarning(type);

                if ((!!positionToAdd || positionToAdd === 0 ) && positionToAdd >= 0 && positionToAdd < this.blocks.length) {
                    this.blocks.splice(positionToAdd, 0, block);
                } else {
                    this.blocks.push(block);
                }
                this.setBlocks(this.blocks);

        }
    }

    /**
     * Updates the list of blocks
     */
    setBlocks(blocks: BlockModel[]): void {
        super.setBlocks(blocks);
        this.wireService.currentResourceModified();
    }

    /**
     * Updates the status of a block type that is limited by count
     * updating the count will change the UI and add a count badge,
     * to not update the count, set includeRemainingCount to false
     */
    private updateLimitedBlockTypeStatus(blockType: BlockTypeModel, limit: number, count: number = 0, includeRemainingCount: boolean = true) {
        if (!blockType) {
            return;
        }
        if (limit) {
            blockType.canAdd = count < limit;
            if (includeRemainingCount){
                blockType.remainingCount = Math.max(limit - count, 0);
            }
        }
    }

    /**
     * Updates the block type statuses of all of the block types
     */
    protected updateBlockTypeStatuses(): void {
        const blockCounts = {};
        for (const block of this.blocks) {
            const key = block.type.key;
            if (!blockCounts[key]) {
                blockCounts[key] = 0;
            }
            blockCounts[key]++;
        }

        this.updateLimitedBlockTypeStatus(
            wireImageBlockType,
            this.imageBlockLimit,
            blockCounts[wireImageBlockType.key],
            false
        );

        this.updateLimitedBlockTypeStatus(
            wireCompanyBioBlockType,
            this.companyBioBlockLimit,
            blockCounts[wireCompanyBioBlockType.key],
            false
        );
        this.updateLimitedBlockTypeStatus(
            wireContactBlockType,
            this.contactBlockLimit,
            blockCounts[wireContactBlockType.key],
            false
        );

        // For companyAddress, companyLogo, if they are set to null, it means related blocks are not added in center panel,
        // Thus, users can click the button to add a new component.
        if (this.focusedWire) {
            wireCompanyLogoBlockType.canAdd = !this.focusedWire.companyLogo;
            wireCompanyAddressBlockType.canAdd = !this.focusedWire.companyAddress;
        }
        this.setDuplicateActionAvailability();
        this.blockTypesProvider.next(this.blockTypes);
    }

    /**
     * Get provider for displaying import tool
     *
     * @returns Observable<boolean>
     */
    public getImportDialogVisibleProvider(): Observable<boolean> {
        return this.importDialogVisibleProvider;
    }

    /**
     * Returns a provider to set the dropdown selected block in the import tool
     */
    public getImportToolDropdownSelectedBlockProvider(): Observable<BlockModel> {
        return this.importToolDropdownSelectedBlockProvider;
    }

    /**
     * Sets the import tool visibility
     *
     * @param visible - true if import tool will be visible
     */
    public setImportDialogVisible(visible: boolean): void {
        this.importDialogVisible = visible;
        this.importDialogVisibleProvider.next(this.importDialogVisible);
    }

    /**
     * Sets the dropdownSelectedBlock on the import tool dialog
     *
     * @param block -  block to set to
     */
    public setImportToolDropdownSelectedBlock(block: BlockModel) {
        this.importToolDropdownSelectedBlockProvider.next(block);
    }

    public setStylesOnBlocks(): void {
        for (const block of this.blocks) {
            if (block?.displayElement?.styles?.styleState) {
                // Set the template if needed
                if (
                    block.displayElement.styles.styleState !== StyleStateEnum.CUSTOM &&
                    block.displayElement.styles.styleState !== StyleStateEnum.DEFAULT
                ) {
                    const savedStyle = this.styles?.find(style => style.id === block.displayElement.styles.styleState);
                    if (savedStyle && block.displayElement.styles.setFromApiModel) {
                        const style: GenericStyleModel = savedStyle.clone().style;
                        style.styleState = savedStyle.id;
                        if (savedStyle.template) {
                            style.template = savedStyle.template;
                        }
                        block.displayElement.styles.setFromApiModel(style);
                    }
                }
            }
        }
    }

    getCompanyLogoHtml(): string {
        if (this.focusedWire.companyLogo?.url) {
            const companyLogoTwigData = Object.assign({}, this.focusedWire.companyLogo);
            return this.twigRenderService.renderTemplate(companyLogoTwigTemplate, companyLogoTwigData);
        } else {
            return '';
        }
    }

    getWireHtml(): string {
        this.updateRenderedOutput();
        let html = '';

        for (const block of this.blocks) {
            if (block.renderedOutput) {
                html += block.renderedOutput;
            }
        }

        if (this.focusedWire.companyAddress ) {
            const companyAddressTwigData = Object.assign({}, this.focusedWire.companyAddress);
            html += this.twigRenderService.renderTemplate(companyAddressTwigTemplate, companyAddressTwigData);
        }

        html = this.processHtml(html);

        return html;
    }

    /**
     * process the body html so that News Direct can display it properly
     * @param html the html on our end
     */
    processHtml(html: string): string {
        const newHtml = html
            // News Direct does not support <br>, we replace it to &nbsp; as all <br> is wrapped inside p tag and can be served as line space
            .replace(/<br>/g, '&nbsp;')
            // News Direct overwrites numbered list style to none, here we set inline style
            .replace(/<ol class="ql-agpr-list">/g, '<ol style="list-style: decimal;">')
            // News Direct sets margin bottom to empty lines with P tag, here we add inline style to overwrite that
            .replace(/<p>/g, '<p style="margin: 0;">')
            .replace(/\r/g, '')
            .replace(/\n/g, '');

        return newHtml;
    }

    getWireHeaderHtml(): string {
        const headerTwigData = Object.assign({}, {
            headline: this.focusedWire.headline,
            subHeadline: this.focusedWire.subHeadline,
            dateline: this.focusedWire.dateline,
            companyName: this.focusedWire.companyName
        });

        return this.twigRenderService.renderTemplate(headerTwigTemplate, headerTwigData);
    }

    updateRenderedOutput(): void {
        this.blocks = this.blocks.map(block => {
            const data: any = {};
            const twigData = Object.assign(data, block.displayElement, block.displayElement.settings);

            // convert /n to <br /> to properly render new line in twig file as company bio uses multi line text area as input
            if (twigData.companyBio) {
                twigData.companyBio = twigData.companyBio.replace(/(?:\r\n|\r|\n)/g, '<br />');
            }

            const renderedOutput = this.twigRenderService.renderTemplate(block.type.twig, twigData);
            block.renderedOutput = renderedOutput;

            return block;
        });
    }

    blockEditingStateChanged(blockModel: BlockModel, isEditing: boolean, blockPosition: string): void {
        blockModel.canMove = !isEditing;
        this.updateMoveability();
    }

    /**
     * validate single block
     */
    validateBlock(block: BlockModel, criteria?: BlockValidationTypeEnum[]) {
        switch (block.type.key) {
            case WireBlockTypeEnum.text:
                if (criteria.find(x => x === BlockValidationTypeEnum.noImageInTextBlock)) {
                    this.wireService.checkErrorsForTextBlock(block);
                }
                break;
            case WireBlockTypeEnum.image:
            case WireBlockTypeEnum.video:
                this.wireService.checkErrorsForImageAndVideoBlock(block);
                break;
            case WireBlockTypeEnum.links:
                this.wireService.checkErrorsForLinksBlock(block);
                break;
        }
    }

    /**
     * deal with deleting fake blocks
     * @param type wire block type
     */
    deleteFakeBlock(type: WireBlockTypeEnum): void {
        switch (type){
            case WireBlockTypeEnum.companyLogo:
                this.focusedWire.companyLogo = null;
                wireCompanyLogoBlockType.canAdd = true;
                break;
            case WireBlockTypeEnum.companyAddress:
                this.focusedWire.companyAddress = null;
                wireCompanyAddressBlockType.canAdd = true;
                break;
        }

        // If the deleted block is the selected block, unselect it
        if (this.selectedBlock && (this.selectedBlock.type.key === type)) {
            this.deselectBlock(false);
        }

        this.fakeBlockDataChange(type);
        this.wireService.currentResourceModified();
    }

    getFakeBlockTwigData(type: WireBlockTypeEnum): any {
        const data: any = {};

        switch (type){
            case WireBlockTypeEnum.companyLogo:
                return Object.assign(data, this.focusedWire.companyLogo);
            case WireBlockTypeEnum.companyAddress:
                return Object.assign(data, this.focusedWire.companyAddress);
        }

        return '';
    }

    getFakeBlockTwigTemplate(type: WireBlockTypeEnum): string {
        switch (type){
            case WireBlockTypeEnum.companyLogo:
                return companyLogoTwigTemplate;
            case WireBlockTypeEnum.companyAddress:
                return companyAddressTwigTemplate;
        }

        return '';
    }

    isFakeBlockRenderable(type: WireBlockTypeEnum): boolean {
        switch (type){
            case WireBlockTypeEnum.companyLogo:
                return !!this.focusedWire.companyLogo?.url;
            case WireBlockTypeEnum.companyAddress:
                return !this.focusedWire.companyAddress?.isFieldEmpty();
        }

        return false;
    }

    selectFakeBlock(type: WireBlockTypeEnum): void {
        switch (type){
            case WireBlockTypeEnum.companyLogo:
                this.selectedBlock = new BlockModel(null, wireCompanyLogoBlockType, null);
                break;
            case WireBlockTypeEnum.companyAddress:
                this.selectedBlock = new BlockModel(null, wireCompanyAddressBlockType, null);
                break;
        }

        this.selectedBlockProvider.next(this.selectedBlock);
    }

    deselectFakeBlock(): void {
        this.selectedBlock = null;
        this.selectedBlockProvider.next(this.selectedBlock);
    }

    getFakeBlockDataChange(): BehaviorSubject<WireBlockTypeEnum> {
        return this.fakeBlockDataChangeProvider;
    }

    fakeBlockDataChange(type: WireBlockTypeEnum): void {
        this.wireService.currentResourceModified();
        this.fakeBlockDataChangeProvider.next(type);
    }

    setDuplicateActionAvailability() {
        this.blocks.forEach(block => {
            block.canDuplicate = true;
            // disable duplicate action of a block if block type reaches its count limit (cannot be added anymore)
            if (this.blockTypes && !this.blockTypes.find(blockType => blockType.key === block.type.key).canAdd) {
                block.canDuplicate = false;
            }
            // for contact block, we don't allow duplicate action
            if (block.type.key === WireBlockTypeEnum.contact) {
                block.canDuplicate = false;
            }
        });
    }

    /**
     * Duplicate a block
     *
     * @param block The block to duplicate
     */
    duplicateBlock(block: BlockModel): void {
        const index = this.blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }
        this.showMultimediaBlockWarning(block.type);
        this.blocks.splice(index, 0, block.clone());
        this.setBlocks(this.blocks);
    }

    // show warning if there are more than three multimedia blocks (images or videos) for US and UK
    showMultimediaBlockWarning(blockType: BlockTypeModel) {
        if (blockType.key === WireBlockTypeEnum.image || blockType.key === WireBlockTypeEnum.video) {
            const hasMultimediaLimit = this.focusedWire.regions
                .some(region => region === WireRegion.united_kingdom || region === WireRegion.united_states);

            let numOfMultimediaBlock  = 0;
            this.blocks.forEach(item => {
                if (item.type.key === WireBlockTypeEnum.image ||  item.type.key === WireBlockTypeEnum.video) {
                    numOfMultimediaBlock++;
                }
            });

            if (hasMultimediaLimit && numOfMultimediaBlock >= 3 ) {
                this.messageService.add({
                    severity: messageSeverityEnum.warning,
                    summary: this.localizationService.transform('Amplify.wire.warning.growlMessage.multimediaLimit.summary'),
                    detail: this.localizationService.transform('Amplify.wire.warning.growlMessage.multimediaLimit.detail')
                });
            }
        }
    }
}
